/*
  Studio Kit motion toolkit (fx.js). Attach effects to any element, in any section, with data attributes; no new
  sections needed. Every effect works on phones (touch) as well as desktop, respects "reduce motion" (final state
  shown, nothing hidden), and shows everything in the theme editor.

  Scroll progress: elements with a scroll effect get the CSS variable --p (0 → 1) as they pass through the screen
  (or through their pinned length), so custom CSS can drive anything from it: transform, clip-path, opacity, colour.

  data-fx="reveal"     entrance when it comes into view. data-fx-style: up | fade | scale | blur | clip-up | clip-left | circle
                       (data-fx-stagger on a parent reveals its [data-fx-item] children one after another)
  data-fx="text"       headline split into words (or lines with data-fx-split="lines") that rise in; add data-fx-scrub to
                       light each word up as you scroll instead (the "reading" effect)
  data-fx="parallax"   drifts against the scroll. data-fx-speed: -1…1 (default .2); halved on phones
  data-fx="zoom"       media settles from a zoom (data-fx-from="1.25") as it crosses the screen
  data-fx="expand"     media grows from inset to full width as you scroll (data-fx-inset="8" % per side)
  data-fx="scrub"      only sets --p: style it yourself in CSS
  data-fx="pin"        the section stays on screen for data-fx-length (in screen heights, default 2; phones use
                       data-fx-length-mobile, default 60% of it) while --p runs 0 → 1; put the stage in [data-fx-pin-stage]
  data-fx="hscroll"    a row of [data-fx-item] slides sideways while the section is pinned; phones get a native swipe row
                       unless data-fx-mobile="pin"
  data-fx="stack"      [data-fx-item] cards stack on top of each other as you scroll (sticky), earlier ones scale back
  data-fx="dock"       the element travels as you scroll and lands in the slot named by data-fx-to (a selector, e.g.
                       "#vessel-slot"); works on phones the same way
  data-fx="counter"    counts up to its number when seen (data-fx-to="1200", data-fx-suffix="+", data-fx-decimals="0")
  data-fx="marquee"    any .marquee track: speeds up with scroll speed and follows scroll direction
  data-fx="magnetic"   pulls toward the pointer (desktop); a soft press on touch
  data-fx="tilt"       3D tilt toward the pointer (desktop); on phones it tilts gently with scroll
  data-fx="spotlight"  a soft light follows the pointer across the element (desktop); on phones it follows scroll
  body[data-fx-cursor="dot|ring"]  a custom cursor on desktop only (never on touch)
*/
(() => {
  if (window.__studioFx) return;
  window.__studioFx = true;
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.body.classList.contains('motion--none');
  const touch = () => window.matchMedia('(hover: none), (pointer: coarse)').matches;
  const phone = () => window.innerWidth < 750;
  const editor = () => !!(window.Shopify && window.Shopify.designMode) && !window.TS_PREVIEW; // Theme Studio's canvas shows the motion
  const num = (v, d) => (v === undefined || v === null || v === '' || isNaN(Number(v)) ? d : Number(v));

  const scrollEls = new Set(); // elements whose --p follows the scroll
  let vh = window.innerHeight;
  let lastY = window.scrollY;
  let velocity = 0;

  // ---------- entrance: reveal, text ----------
  const seen = new IntersectionObserver((entries) => entries.forEach((en) => {
    if (!en.isIntersecting) return;
    en.target.classList.add('fx-in');
    if (en.target.dataset.fx === 'counter') runCounter(en.target);
    seen.unobserve(en.target);
  }), { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

  function splitText(el) {
    if (el.dataset.fxOrig !== undefined) return;
    el.dataset.fxOrig = el.innerHTML;
    let i = 0;
    const walk = (node) => {
      for (const n of [...node.childNodes]) {
        if (n.nodeType === 3) {
          const frag = document.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            const w = document.createElement('span'); w.className = 'fx-w';
            const inner = document.createElement('span'); inner.textContent = part; inner.style.setProperty('--i', String(i++));
            w.appendChild(inner); frag.appendChild(w);
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1 && !/^(BR|SVG|IMG)$/i.test(n.tagName)) walk(n);
      }
    };
    walk(el);
    el.style.setProperty('--n', String(i));
    el.classList.add('fx-split');
  }

  function runCounter(el) {
    const to = num(el.dataset.fxTo, num(el.textContent.replace(/[^\d.-]/g, ''), 0));
    const dec = num(el.dataset.fxDecimals, 0);
    const suffix = el.dataset.fxSuffix || '';
    const prefix = el.dataset.fxPrefix || '';
    if (reduce()) { el.textContent = `${prefix}${to.toFixed(dec)}${suffix}`; return; }
    const t0 = performance.now(); const dur = num(el.dataset.fxDuration, 1400);
    const step = (t) => {
      const k = clamp((t - t0) / dur); const e = 1 - Math.pow(1 - k, 3);
      el.textContent = `${prefix}${(to * e).toFixed(dec)}${suffix}`;
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  // ---------- scroll-linked: progress per element ----------
  function rangeOf(el) {
    // pinned effects: progress over the pinned length; others: from entering the bottom to leaving the top
    const r = el.getBoundingClientRect();
    const pinned = ['pin', 'hscroll'].includes(el.dataset.fx) || (el.dataset.fx === 'stack');
    if (pinned) return clamp(-r.top / Math.max(1, r.height - vh));
    if (el.dataset.fx === 'dock') return null;
    return clamp((vh - r.top) / (vh + r.height));
  }

  function setPinLength(el) {
    const len = phone() ? num(el.dataset.fxLengthMobile, num(el.dataset.fxLength, 2) * 0.6) : num(el.dataset.fxLength, 2);
    el.style.setProperty('--fx-len', String(Math.max(1, len)));
  }

  function hscrollSetup(el) {
    const track = el.querySelector('[data-fx-track]') || el.querySelector('[data-fx-item]')?.parentElement;
    if (!track) return;
    track.classList.add('fx-track');
    el._track = track;
    const usePin = !phone() || el.dataset.fxMobile === 'pin';
    el.classList.toggle('fx-hscroll--pinned', usePin);
    if (usePin) {
      const extra = Math.max(0, track.scrollWidth - el.clientWidth);
      el.style.setProperty('--fx-len', String(1 + extra / vh));
      el._dist = extra;
    } else { el.style.removeProperty('--fx-len'); el._dist = 0; track.style.transform = ''; }
  }

  // dock: the element moves from where it is to its slot, between "start" (element at the top third of the screen)
  // and "end" (slot in the middle of the screen)
  function dockMeasure(el) {
    const slot = document.querySelector(el.dataset.fxTo || '');
    if (!slot) { el._dock = null; return; }
    el.style.transform = '';
    const a = el.getBoundingClientRect(); const b = slot.getBoundingClientRect();
    const sy = window.scrollY;
    el._dock = {
      slot,
      dx: (b.left + b.width / 2) - (a.left + a.width / 2),
      dy: (b.top + sy + b.height / 2) - (a.top + sy + a.height / 2),
      s: b.width && a.width ? b.width / a.width : 1,
      start: a.top + sy - vh * 0.25,
      end: b.top + sy + b.height / 2 - vh * 0.55,
    };
    slot.classList.add('fx-dock-slot');
  }

  function tick() {
    ticking = false;
    const y = window.scrollY;
    velocity = velocity * 0.8 + (y - lastY) * 0.2;
    lastY = y;
    for (const el of scrollEls) {
      const fx = el.dataset.fx;
      if (fx === 'dock') {
        const d = el._dock;
        if (!d) continue;
        const p = clamp((y - d.start) / Math.max(1, d.end - d.start));
        const e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        el.style.transform = `translate3d(${(d.dx * e).toFixed(1)}px, ${(d.dy * e).toFixed(1)}px, 0) scale(${(1 + (d.s - 1) * e).toFixed(3)})`;
        el.style.setProperty('--p', e.toFixed(4));
        el.classList.toggle('fx-docked', p >= 1);
        d.slot.classList.toggle('fx-dock-landed', p >= 1);
        continue;
      }
      const r = el.getBoundingClientRect();
      if (r.bottom < -vh || r.top > vh * 2) continue;
      const p = rangeOf(el);
      el.style.setProperty('--p', p.toFixed(4));
      if (fx === 'parallax') {
        const sp = num(el.dataset.fxSpeed, 0.2) * (phone() ? 0.5 : 1);
        el.style.translate = `0 ${((0.5 - p) * sp * 100).toFixed(2)}px`;
      } else if (fx === 'hscroll' && el._track && el._dist) {
        el._track.style.transform = `translate3d(${(-el._dist * p).toFixed(1)}px, 0, 0)`;
      } else if (fx === 'stack') {
        const items = el._items || (el._items = $$('[data-fx-item]', el));
        const n = items.length;
        items.forEach((it, i) => {
          const local = clamp(p * n - i - 1, 0, 1); // how far the next card has covered this one
          it.style.setProperty('--covered', local.toFixed(3));
        });
      } else if (fx === 'text' && el.hasAttribute('data-fx-scrub')) {
        el.style.setProperty('--p', clamp((p - 0.15) / 0.5).toFixed(4));
      } else if (fx === 'tilt' && touch()) {
        el.style.setProperty('--rx', `${((0.5 - p) * 8).toFixed(2)}deg`);
      } else if (fx === 'spotlight' && touch()) {
        el.style.setProperty('--mx', '50%'); el.style.setProperty('--my', `${(p * 100).toFixed(1)}%`);
      }
    }
    // marquees follow the scroll speed and direction
    for (const m of marquees) {
      const boost = clamp(Math.abs(velocity) / 12, 0, 3);
      m.style.setProperty('--fx-speed', (1 + boost).toFixed(2));
      if (Math.abs(velocity) > 2) m.classList.toggle('fx-reverse', velocity < 0);
    }
    if (Math.abs(velocity) > 0.3 && marquees.length) req();
  }
  let ticking = false;
  const req = () => { if (!ticking) { ticking = true; requestAnimationFrame(tick); } };
  let marquees = [];

  // ---------- pointer effects (desktop) ----------
  function pointerFx(el) {
    const fx = el.dataset.fx;
    if (touch()) {
      if (fx === 'magnetic') {
        el.addEventListener('touchstart', () => el.classList.add('fx-pressed'), { passive: true });
        el.addEventListener('touchend', () => el.classList.remove('fx-pressed'), { passive: true });
      }
      if (fx === 'tilt' || fx === 'spotlight') scrollEls.add(el);
      return;
    }
    el.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width; const y = (e.clientY - r.top) / r.height;
      if (fx === 'magnetic') { const k = num(el.dataset.fxStrength, 0.3); el.style.translate = `${((x - 0.5) * r.width * k).toFixed(1)}px ${((y - 0.5) * r.height * k).toFixed(1)}px`; }
      if (fx === 'tilt') { el.style.setProperty('--rx', `${((0.5 - y) * 10).toFixed(2)}deg`); el.style.setProperty('--ry', `${((x - 0.5) * 12).toFixed(2)}deg`); }
      if (fx === 'spotlight') { el.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`); el.style.setProperty('--my', `${(y * 100).toFixed(1)}%`); }
    });
    el.addEventListener('pointerleave', () => { el.style.translate = ''; el.style.setProperty('--rx', '0deg'); el.style.setProperty('--ry', '0deg'); });
  }

  function cursor() {
    const kind = document.body.dataset.fxCursor;
    if (!kind || touch() || reduce() || document.querySelector('.fx-cursor')) return;
    const c = document.createElement('div'); c.className = `fx-cursor fx-cursor--${kind}`; c.setAttribute('aria-hidden', 'true');
    document.body.appendChild(c);
    let x = -100, y = -100, cx = -100, cy = -100;
    addEventListener('pointermove', (e) => { x = e.clientX; y = e.clientY; c.classList.toggle('is-link', !!e.target.closest('a, button, [data-fx="magnetic"]')); }, { passive: true });
    const loop = () => { cx += (x - cx) * 0.22; cy += (y - cy) * 0.22; c.style.transform = `translate3d(${cx}px, ${cy}px, 0)`; requestAnimationFrame(loop); };
    requestAnimationFrame(loop);
  }

  // ---------- set up ----------
  function init(scope = document) {
    const showAll = reduce() || editor();
    document.documentElement.classList.add('fx-ready');
    if (showAll) document.documentElement.classList.add('fx-static');
    for (const el of $$('[data-fx]', scope)) {
      if (el._fx) continue;
      el._fx = true;
      const fx = el.dataset.fx;
      if (fx === 'text') splitText(el);
      if (fx === 'reveal' || fx === 'text' || fx === 'counter') { if (showAll) { el.classList.add('fx-in'); if (fx === 'counter') runCounter(el); } else seen.observe(el); }
      if (el.hasAttribute('data-fx-stagger')) $$('[data-fx-item]', el).forEach((it, i) => it.style.setProperty('--i', String(i)));
      if (showAll) { if (['pin', 'hscroll', 'stack'].includes(fx)) el.style.setProperty('--p', '1'); continue; }
      if (fx === 'pin' || fx === 'stack') setPinLength(el);
      if (fx === 'hscroll') hscrollSetup(el);
      if (fx === 'dock') dockMeasure(el);
      if (['parallax', 'zoom', 'expand', 'scrub', 'pin', 'hscroll', 'stack', 'dock'].includes(fx) || (fx === 'text' && el.hasAttribute('data-fx-scrub'))) scrollEls.add(el);
      if (['magnetic', 'tilt', 'spotlight'].includes(fx)) pointerFx(el);
    }
    marquees = $$('[data-fx="marquee"]');
    if (!showAll) cursor();
    // Safety net: anything on screen that an observer missed is shown.
    setTimeout(() => $$('[data-fx="reveal"]:not(.fx-in), [data-fx="text"]:not(.fx-in)').forEach((el) => { const r = el.getBoundingClientRect(); if (r.top < vh && r.bottom > 0) el.classList.add('fx-in'); }), 1600);
    req();
  }

  const remeasure = () => {
    vh = window.innerHeight;
    for (const el of scrollEls) {
      if (el.dataset.fx === 'hscroll') hscrollSetup(el);
      if (el.dataset.fx === 'pin' || el.dataset.fx === 'stack') setPinLength(el);
    }
    for (const el of scrollEls) if (el.dataset.fx === 'dock') dockMeasure(el);
    req();
  };
  addEventListener('scroll', req, { passive: true });
  let rt;
  addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(remeasure, 150); });
  addEventListener('load', remeasure);
  document.addEventListener('shopify:section:load', (e) => { init(e.target); remeasure(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  window.StudioFx = { init, remeasure };
})();
